import * as Signals from 'signals';
import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';

// Components
import 'loading';
import "fontAwesomeBindings";
import "Content/ts/Shared/Buttons/CloseModal/close-modal-action";
import "Content/ts/Shared/Loading/loading-base";

// Services
import { MembershipService } from 'Content/ts/Membership/Services/membershipService';
import { ValidationService } from 'Content/ts/Shared/Services/Validation/validationService';

// Interfaces
import { IPriceBreakdownDetailsModel } from 'Content/ts//Models/Shared/PriceBreakdown/IPriceBreakdownDetailsModel';

// Models
import { PriceBreakdownDetailsModel } from 'Content/ts/Models/Shared/PriceBreakdown/PriceBreakdownDetailsModel';
import { IMembershipPriceDetailsRequestModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPriceDetailsRequestModel';
import { IMembershipPriceDetailsResponseModel } from 'Content/ts/Models/Join/Interfaces/IMembershipPriceDetailsResponseModel';

export const Deps = {
    MembershipService: MembershipService
}

// Price details passed in as an observable or a PriceRequestGroupId 
// is passed in and the price details are fetched
export interface IPriceBreakdownParams {
    priceRequestGroupId: number;
    promoDiscount: KnockoutObservable<number>;
    priceDetails: PriceBreakdownDetailsModel;
    isLoading: KnockoutObservable<boolean>;
    promoEnabled: KnockoutObservable<boolean>;
    promoCode: KnockoutObservable<string>;
    priceRequest: KnockoutObservable<IMembershipPriceDetailsRequestModel>;
    showMonthly: boolean;
    showToday: boolean;
    showDiscounts: boolean;
    clearSignal: Signals.Signal;
}

export default class PriceBreakdown {
    customMessages: {
        AddPromoCode: string;
        RemovePromoCode: string;
        PromoCode: string;
        EnterPromoCodeBelow: string;
        ModalTitle: string;
        InvalidPromoCode: string;
        Discount: string;
        LoadingPromoPrice: string;
        PromoCodeSuccess: string;
        EnterPromoCode: string;
        TotalDiscount: string;
        Promotion: string;
        PriceLoadingText: string;
    };

    priceRequestGroupId: number;
    basePriceRequestGroupId: number;
    showDiscounts: boolean;

    priceRequest: KnockoutObservable<IMembershipPriceDetailsRequestModel>;
    basePriceDetails: IPriceBreakdownDetailsModel;

    priceDetails: PriceBreakdownDetailsModel;
    isLoading: KnockoutObservable<boolean>;
    isLoadingPromoPrice: KnockoutObservable<boolean>;
    showMonthly: KnockoutObservable<boolean>;
    showToday: KnockoutObservable<boolean>;

    promoEnabled: KnockoutObservable<boolean>;
    promoCodeModal: KnockoutObservable<string>;
    promoCode: KnockoutObservable<string>;
    showAddPromoCodeModal: KnockoutObservable<boolean>;
    invalidPromoCode: KnockoutObservable<boolean>;
    promoCodeDescription: KnockoutObservable<string>;
    promoDiscount: KnockoutObservable<number>;
    tempPromoDiscount: KnockoutObservable<number>;

    hasMonthlyFee: KnockoutComputed<boolean>;
    hasPromoCodeModal: KnockoutComputed<boolean>;
    canAddPromoCodeModal: KnockoutComputed<boolean>;
    totalDueNow: KnockoutComputed<number>;
    automaticDiscount: KnockoutComputed<number>;
    promoCodeSet: KnockoutComputed<boolean>;
    displayInvalidPromoCode: KnockoutComputed<boolean>;
    iconVisible: KnockoutComputed<boolean>;
    promoCodeSuccess: KnockoutComputed<boolean>;
    discount: KnockoutComputed<number>;
    automaticDiscountDescription: KnockoutComputed<string>;
    multiDiscount: KnockoutComputed<boolean>;
    showTotalDiscount: KnockoutComputed<boolean>;
    showAutomaticDiscount: KnockoutComputed<boolean>;
    hasBasePrice: KnockoutComputed<boolean>;

    validationFields: KnockoutObservable<any>[];

    constructor(params: IPriceBreakdownParams) {
        this.priceRequestGroupId = params.priceRequestGroupId;
        this.priceDetails = params.priceDetails ||
            new PriceBreakdownDetailsModel({
                CycleFee: null,
                FirstBillDate: null,
                PriceRequestGroupId: null,
                BillingMethodId: null,
                BillingCycleId: null,
                MCD: null,
                AutomaticDiscount: null,
                Fees: []
            });

        this.priceRequest = params.priceRequest;
        this.showMonthly = params.showMonthly != null ? ko.observable(params.showMonthly) : ko.observable(true);
        this.showToday = params.showToday != null ? ko.observable(params.showToday) : ko.observable(true);
        this.promoEnabled = params.promoEnabled || ko.observable(false);
        this.isLoading = params.isLoading || ko.observable(false);
        this.showDiscounts = params.showDiscounts || false;
        this.promoCode = params.promoCode || ko.observable(null);
        this.promoDiscount = params.promoDiscount || ko.observable(0);

        // need to store the base price to revert promo code discounts
        this.basePriceDetails = {
            CycleFee: null,
            FirstBillDate: null,
            PriceRequestGroupId: null,
            BillingMethodId: null,
            BillingCycleId: null,
            MCD: null,
            AutomaticDiscount: null,
            Fees: []
        };
        this.tempPromoDiscount = ko.observable<number>();
        this.isLoadingPromoPrice = ko.observable(false);
        this.promoCodeModal = ko.observable("").extend({ throttle: 500 });
        this.showAddPromoCodeModal = ko.observable(false);
        this.invalidPromoCode = ko.observable(false);
        this.promoCodeDescription = ko.observable("");
        this.basePriceRequestGroupId = 0;
        this.validationFields = [];

        this.setupComputeds();
        this.setupValidation();
        this.setupSubscriptions();
        this.retrieveCustomMessages();

        if (this.priceRequestGroupId) {
            this.getPriceDetails(this.priceRequestGroupId);
        }

        if (params.clearSignal) {
            params.clearSignal.add(() => this.clearPromoCode());
        }
    }

    private setupComputeds() {
        this.hasBasePrice = ko.computed(() => {
            return this.basePriceDetails.Fees.length > 0;
        });

        this.hasMonthlyFee = ko.computed(() => {
            return this.priceDetails.HasFees() && this.priceDetails.IsDirectDebit();
        });

        this.promoCodeSet = ko.computed(() => {
            return this.promoCode() != null && this.promoCode().length > 0;
        });

        this.displayInvalidPromoCode = ko.computed(() => {
            return this.invalidPromoCode() && this.promoCodeModal.isValid();
        });

        this.promoCodeSuccess = ko.computed(() => {
            return this.hasBasePrice() &&
                !this.isLoadingPromoPrice() &&
                !this.invalidPromoCode() &&
                this.hasPromoCodeModal();
        });

        this.totalDueNow = ko.computed(() => {
            if (this.priceDetails.HasFees()) {
                const total = this.priceDetails.TotalPrice();
                return this.showDiscounts ? total - this.discount() : total;
            } else {
                return 0;
            }
        });

        // Promo Code Modal

        this.hasPromoCodeModal = ko.computed(() => {
            return this.promoCodeModal().length > 0;
        });

        this.canAddPromoCodeModal = ko.computed(() => {
            return this.hasPromoCodeModal() && !this.invalidPromoCode() && !this.isLoadingPromoPrice();
        });

        this.automaticDiscount = ko.computed(() => {
            return this.priceDetails.HasFees()
                ? this.priceDetails.AutomaticDiscount()
                : 0;
        });

        this.automaticDiscountDescription = ko.computed(() => {
            return this.automaticDiscount() > 0
                ? this.priceDetails.Fees().flatMap(f => f.Discounts).find(f => f.Name && !f.PromoCode).Name
                : "";
        });

        this.discount = ko.computed(() => {
            return this.promoDiscount() + this.automaticDiscount();
        });

        this.multiDiscount = ko.computed(() => {
            return this.showDiscounts && this.automaticDiscount() > 0 && (!this.promoEnabled() || this.promoCodeSet());
        });

        this.showTotalDiscount = ko.computed(() => {
            return this.showDiscounts && this.discount() > 0 && this.promoCodeSet();
        });

        this.showAutomaticDiscount = ko.computed(() => {
            return this.showDiscounts && this.automaticDiscount() > 0;
        });
    };

    private setupSubscriptions() {
        this.promoCodeModal.subscribe(() => {
            this.resetPromoModal();
            if (this.promoCodeModal.isValid()) {
                const request = this.priceRequest();
                request.PromoCode = this.promoCodeModal();

                this.isLoadingPromoPrice(true);
                Deps.MembershipService.getAgreementPrice(request)
                    .then((response: IMembershipPriceDetailsResponseModel) => {
                        if (response != null) {
                            // We currently don't support promo codes which only return a future discount, the below check for PromoCode
                            // ensures we do not try to update the UI if only a future discount is returned to prevent error
                            // and instead calls the fallback method
                            const discountFees = response.Fees.flatMap(f => f.Discounts)
                                .filter(d => d.Name && d.PromoCode);

                            if (discountFees.length > 0) {
                                this.invalidPromoCode(false);
                                this.basePriceDetails = Deps.MembershipService.getMembershipPriceBreakdownModel(response, this.priceDetails.AutomaticDiscount());
                                this.promoCodeDescription(discountFees.find(x => x.PromoCode != null).Name);
                                this.tempPromoDiscount(Math.abs(discountFees.map(f => f.Amount).reduce((a, b) => a + b) - this.priceDetails.AutomaticDiscount()));
                            } else {
                                return Deps.MembershipService.getPromotionForNewMembership(request)
                                    .then(promoResponse => {
                                        if (promoResponse.PromotionValid) {
                                            this.invalidPromoCode(false);
                                            this.basePriceDetails = Deps.MembershipService.getMembershipPriceBreakdownModel(response, this.priceDetails.AutomaticDiscount());
                                            this.promoCodeDescription(promoResponse.PromotionName);
                                        }
                                        else {
                                            this.invalidPromoCode(true);
                                        }

                                    });
                            }
                        }
                    })
                    .catch(() => {
                        this.invalidPromoCode(true);
                    })
                    .finally(() => {
                        this.isLoadingPromoPrice(false);
                    });
            }
        });
    };

    private setupValidation() {
        ValidationService.AddValidationRule(this.promoCodeModal, this.validationFields, {
            required: {
                params: true
            }
        });
    };

    private retrieveCustomMessages() {
        this.customMessages = {
            AddPromoCode: localisationHelper.getString("JoinResources", "AddPromoCode"),
            RemovePromoCode: localisationHelper.getString("JoinResources", "RemovePromoCode"),
            ModalTitle: localisationHelper.getString("JoinResources", "RedeemPromoCode"),
            PromoCode: localisationHelper.getString("JoinResources", "PromoCode"),
            EnterPromoCodeBelow: localisationHelper.getString("JoinResources", "EnterPromoCodeBelow"),
            InvalidPromoCode: localisationHelper.getString("JoinResources", "InvalidPromoCode"),
            Discount: localisationHelper.getString("GenericResources", "Discount"),
            LoadingPromoPrice: localisationHelper.getString("JoinResources", "LoadingPromoPrice"),
            PromoCodeSuccess: localisationHelper.getString("JoinResources", "PromoPriceSuccess"),
            EnterPromoCode: localisationHelper.getString("JoinResources", "EnterPromoCode"),
            TotalDiscount: localisationHelper.getString("GenericResources", "TotalDiscount"),
            Promotion: localisationHelper.getString("GenericResources", "Promotion"),
            PriceLoadingText: localisationHelper.getString("GenericResources", "PriceDetailsLoading")
        };
    };

    private getPriceDetails(priceRequestGroupId: number) {
        this.isLoading(true);

        const id = priceRequestGroupId > 0 ? priceRequestGroupId : this.priceRequestGroupId;

        Deps.MembershipService.getMembershipPrice(id)
            .then((priceDetails: IMembershipPriceDetailsResponseModel) => {
                this.updatePrice(Deps.MembershipService.getMembershipPriceBreakdownModel(priceDetails, this.priceDetails.AutomaticDiscount()));
                this.isLoading(false);
            });
    };

    closePromoModal = () => {
        this.promoCodeModal("");
        this.showAddPromoCodeModal(false);
        this.promoDiscount(0);
    };

    showPromoModal = () => {
        this.closePromoModal();
        this.resetPromoModal();
        ValidationService.HideValidationMessage(this.promoCodeModal);
        this.showAddPromoCodeModal(true);
    };

    resetPromoModal = () => {
        this.resetBasePrice();
        this.tempPromoDiscount(null);
        this.invalidPromoCode(false);
        this.promoCodeDescription("");
    };

    addPromoCode = () => {
        if (this.canAddPromoCodeModal()) {
            this.basePriceRequestGroupId = this.priceDetails.PriceRequestGroupId();

            this.updatePrice(this.basePriceDetails);
            this.promoDiscount(this.tempPromoDiscount());
            this.promoCode(this.promoCodeModal());

            this.resetBasePrice();
            this.tempPromoDiscount(null);
            this.promoCodeModal("");
            this.showAddPromoCodeModal(false);
        }
    };

    clearPromoCode = () => {
        this.promoCode(null);
        this.promoDiscount(0);
    };

    removePromoCode = () => {
        this.clearPromoCode();
        this.getPriceDetails(this.basePriceRequestGroupId);
    };

    private updatePrice(model: IPriceBreakdownDetailsModel) {
        this.priceDetails.CycleFee(model.CycleFee);
        this.priceDetails.BillingMethodId(model.BillingMethodId);
        this.priceDetails.BillingCycleId(model.BillingCycleId);
        this.priceDetails.PriceRequestGroupId(model.PriceRequestGroupId);
        this.priceDetails.FirstBillDate(model.FirstBillDate);
        this.priceDetails.MCD(model.MCD);
        this.priceDetails.AutomaticDiscount(model.AutomaticDiscount);
        this.priceDetails.Fees(model.Fees);
    }

    private resetPrice() {
        this.updatePrice({
            CycleFee: null,
            FirstBillDate: null,
            PriceRequestGroupId: null,
            BillingMethodId: null,
            BillingCycleId: null,
            MCD: null,
            AutomaticDiscount: null,
            Fees: []
        });
    }

    private resetBasePrice() {
        this.basePriceDetails = {
            CycleFee: null,
            FirstBillDate: null,
            PriceRequestGroupId: null,
            BillingMethodId: null,
            BillingCycleId: null,
            MCD: null,
            AutomaticDiscount: null,
            Fees: []
        };
    }
}

const componentName = 'price-breakdown';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: PriceBreakdown,
    template: { require: `text!/Themes/Default/Content/ts/Shared/PriceBreakdown/${componentName}.html` }
});